import React, { useEffect, useMemo, useState } from "react";
import "./Users.css";
import Table from "../../../components/reusable-table/Table";
import {
  EditIcon,
  LuNotebookPenIcon,
  MessageSquareIcon,
  MoreVerticalIcon,
  OpenInNewIcon,
  SearchIcon,
} from "../../../icons/reactIcons";
import Input from "../../../components/reusable-input/Input";
import MenuItem from "../../../components/reusable-menuitem/MenuItem";
import UserModal from "./user-modal/UserModal";
import ConfirmUserActionModal from "../../../components/confirm-modal/ConfirmUserActionModal";
import { Outlet, useNavigate } from "react-router-dom";
import StatusChip from "../../../components/custom-statusChip/StatusChip";
import EditUserModal from "./user-modal/EditUserModal";
import { useAppDispatch, useAppSelector } from "../../../redux/store/hooks";
import {
  createUserFeedback,
  fetchUserList,
  updateUser,
} from "../../../redux/slices/userListSlice";
import { updateUserRequest } from "../../../redux/slices/userRequestSlice";
import { sendCmsMessage } from "../../../redux/slices/cmsMessageSlice";
import { formatDateTime } from "../../../utils/formatDateTime";
import { getLastActiveLabel } from "../../../utils/getLastActivity";
import { setUserTab } from "../../../redux/slices/userNavigationSlice";

const commonColumns = [
  { header: "Name", accessor: "displayName" },
  { header: "Study ID", accessor: "studyId" },
  { header: "Week", accessor: "week" },
  { header: "Group Name", accessor: "groupName" },
];

const activeInactiveColumns = [
  ...commonColumns,
  { header: "Progress", accessor: "progress" },
  { header: "Last Active", accessor: "lastActive" },
];

const deletedColumns = [
  ...commonColumns,
  { header: "Deleted By", accessor: "deletedInfo" },
];

const Users: React.FC = () => {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState<{
    top: number;
    right: number;
  }>({ top: 0, right: 0 });
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [search, setSearch] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [messageUser, setMessageUser] = useState<any>(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [confirmActionType, setConfirmActionType] = useState<
    "delete" | "activate"
  >("delete");
  const [confirmUser, setConfirmUser] = useState<any>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editUser, setEditUser] = useState<any>(null);
  const [modalType, setModalType] = useState<"message" | "feedback">("message");
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [pageMap, setPageMap] = useState({
    Active: 1,
    Inactive: 1,
    Deleted: 1,
  });
  const rowsPerPage = 30;

  const tab = useAppSelector((state) => state.userNavigation.tab);

  const handleNameClick = (row: any) => {
    dispatch(setUserTab(tab));
    navigate(`/users/${row.name}/${row.user_id}`);
  };

  const adminDetail = JSON.parse(localStorage.getItem("adminDetail") || "{}");
  const userRole = adminDetail.role || "";
  const isStudyTeam = userRole === "study team";

  const dispatch = useAppDispatch();
  const {
    users,
    loading,
    feedbackCreateLoading,
    totalCount,
    activeCount,
    inactiveCount,
    deletedCount,
    updateUserLoading,
  } = useAppSelector((state) => state.userList);

  const { createLoading: messageCreateLoading } = useAppSelector(
    (state) => state.cmsMessages,
  );

  const getApiStatus = () => {
    if (tab === "Active") return "active";
    if (tab === "Inactive") return "inactive";
    return "delete";
  };

  useEffect(() => {
    dispatch(
      fetchUserList({
        pageId: pageMap[tab],
        limit: rowsPerPage,
        status:
          tab === "Active"
            ? "active"
            : tab === "Inactive"
              ? "inactive"
              : "delete",
      }),
    );
  }, [dispatch, tab, pageMap]);

  const handleMoreClick = (e: React.MouseEvent, row: any) => {
    const rect = (e.target as HTMLElement).getBoundingClientRect();
    setMenuPosition({
      top: rect.top + window.scrollY,
      right: 120,
    });
    setSelectedRow(row);
    setMenuOpen(true);
  };

  const handleCloseMenu = () => {
    setMenuOpen(false);
    setSelectedRow(null);
  };

  const handleOpenModal = (row: any, type: "message" | "feedback") => {
    setSelectedUser(row);
    setModalType(type);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setMessageUser(null);
  };

  const handleSend = async (message: string) => {
    if (!selectedUser) return;

    try {
      if (modalType === "message") {
        await dispatch(
          sendCmsMessage({
            message,
            users: [
              {
                id: String(selectedUser.user_id),
                isActive: "true",
              },
            ],
          }),
        ).unwrap();
      } else {
        await dispatch(
          createUserFeedback({
            userId: selectedUser.user_id,
            feedback: message,
          }),
        ).unwrap();
      }

      handleCloseModal();
    } catch (error) {
      console.error("Send action failed:", error);
      // Modal stays open for retry
    }
  };

  const handleOpenConfirmModal = (type: "delete" | "activate", row: any) => {
    setConfirmActionType(type);
    console.log("row", row);
    setConfirmUser(row); // store the specific row for confirmation
    setIsConfirmModalOpen(true);
    handleCloseMenu();
  };

  const handleConfirmAction = async () => {
    if (!confirmUser) return;

    try {
      await dispatch(
        updateUserRequest({
          userId: confirmUser.__raw.id, // IMPORTANT: use backend user id
          status: confirmActionType === "delete" ? "delete" : "activate",
          rejectReason: undefined,
        }),
      ).unwrap();

      // Optional: refetch users after delete
      dispatch(
        fetchUserList({
          pageId: pageMap[tab],
          limit: rowsPerPage,
          status: getApiStatus(),
        }),
      );

      setIsConfirmModalOpen(false);
      setConfirmUser(null);
    } catch (err) {
      console.error("Delete failed:", err);
    }
  };

  const allUsers = useMemo(() => {
    return users.map((u) => ({
      user_id: u.id,
      name: `${u.firstName} ${u.lastName}`,
      studyId: u.studyId,
      week: (
        <span style={{ whiteSpace: "nowrap" }}>{u.week ? u.week : "-"}</span>
      ),
      email: u.email,
      mobileNumber: u.mobileNumber,
      groupName: (
        <div
          className="user-table-group"
          style={{ border: u.group?.name ? "2px solid #ccc" : "" }}
        >
          {u.group?.name || "-"}
        </div>
      ),
      progress: (() => {
        const progressValue = Math.round(Number(u.progress) || 0);

        let color = "#FF0000";
        let bg = "rgba(255, 0, 0, 0.10)";

        if (progressValue >= 50) {
          color = "#008000"; // Green
          bg = "rgba(0, 128, 0, 0.10)";
        } else if (progressValue >= 35) {
          color = "#E6A700"; // Yellow
          bg = "rgba(230, 167, 0, 0.10)";
        }

        return (
          <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
            <span
              className="user-table-completion-status"
              style={{ border: "2px solid #ccc" }}
            >
              {u.completedLessons}/{u.totalLessonsInWeek} Completed
            </span>
            <StatusChip
              status={`${progressValue} %`}
              color={color}
              backgroundcolor={bg}
            />
          </div>
        );
      })(),
      lastActive: u.lastActivity
        ? (() => {
            const lastDate = new Date(u.lastActivity);
            const today = new Date();

            lastDate.setHours(0, 0, 0, 0);
            today.setHours(0, 0, 0, 0);

            const diffInDays =
              (today.getTime() - lastDate.getTime()) / (1000 * 60 * 60 * 24);

            let color = "#008000";
            let bg = "rgba(0,128,0,0.10)";

            if (diffInDays === 3) {
              color = "#E6A700"; // Yellow
              bg = "rgba(230,167,0,0.10)";
            }

            if (diffInDays >= 4) {
              color = "#FF0000"; // Red
              bg = "rgba(255,0,0,0.10)";
            }

            return (
              <StatusChip
                status={getLastActiveLabel(u.lastActivity)}
                color={color}
                backgroundcolor={bg}
              />
            );
          })()
        : "-",
      deletedInfo: u.isDeleted ? (
        <div className="merged-cell">
          <div className="merged-label">{u.updater?.name || "-"}</div>
          <div className="merged-date">
            {u.statusUpdatedDate ? formatDateTime(u.statusUpdatedDate) : "-"}
          </div>
        </div>
      ) : (
        "-"
      ),
      __raw: u, // keep original reference if needed later
    }));
  }, [users]);

  const getFilteredData = () => {
    let data: any[] = allUsers;

    // If search empty → return all
    if (search.trim() === "") return data;

    return data.filter(
      (u) =>
        u.name.toLowerCase().includes(search.toLowerCase()) ||
        u.studyId.toLowerCase().includes(search.toLowerCase()),
    );
  };

  // Actions differ per tab
  const actions = isStudyTeam
    ? [
        {
          label: <EditIcon style={{ fontSize: "20px" }} />,
          onClick: (row: any) => handleEditUser(row),
        },
        {
          label: <MessageSquareIcon style={{ fontSize: "20px" }} />,
          onClick: (row: any) => handleOpenModal(row, "message"),
        },
        {
          label: <LuNotebookPenIcon style={{ fontSize: "20px" }} />,
          onClick: (row: any) => handleOpenModal(row, "feedback"),
        },
      ]
    : tab === "Deleted"
      ? [
          {
            label: <MoreVerticalIcon />,
            onClick: (row: any, e: React.MouseEvent) => handleMoreClick(e, row),
          },
        ]
      : [
          {
            label: <MessageSquareIcon />,
            onClick: (row: any) => handleOpenModal(row, "message"),
          },
          {
            label: <MoreVerticalIcon />,
            onClick: (row: any, e: React.MouseEvent) => handleMoreClick(e, row),
          },
        ];

  const clickableData = getFilteredData().map((user) => ({
    ...user,
    displayName: (
      <>
        {tab !== "Deleted" ? (
          <span
            className="clickable-name"
            style={{
              color: "#fa2f97",
              cursor: "pointer",
              textDecoration: "underline",
            }}
            onClick={() => handleNameClick(user)}
          >
            {user.name} <OpenInNewIcon />
          </span>
        ) : (
          <span className="clickable-name">{user.name}</span>
        )}
      </>
    ),
  }));

  const handleEditUser = (row: any) => {
    const raw = row.__raw;

    setEditUser({
      id: raw.id,
      firstName: raw.firstName,
      lastName: raw.lastName,
      email: raw.email,
      mobileNumber: raw.mobileNumber,
    });

    setIsEditModalOpen(true);
  };

  // if (loading) return <Loader />;

  return (
    <div className="users-container">
      <div className="Common-header-area">
        <h1 className="page-title" style={{ margin: 0 }}>
          Users
        </h1>
        <div className="user-search">
          <Input
            type="text"
            placeholder="Search Users"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            search={true}
            startIcon={<SearchIcon />}
            removeBottomMargin
          />
        </div>
      </div>
      <div className="user-header-actions">
        <div className="tabs">
          {[
            { label: "Active", count: activeCount },
            { label: "Inactive", count: inactiveCount },
            !isStudyTeam && { label: "Deleted", count: deletedCount },
          ]
            .filter(Boolean)
            .map((t: any) => (
              <button
                key={t.label}
                className={`tab-btn ${tab === t.label ? "active" : ""}`}
                onClick={() => dispatch(setUserTab(t.label))}
              >
                {t.label} <span className="tab-count">{t.count}</span>
              </button>
            ))}
        </div>
      </div>

      {/* Table with dynamic columns */}
      <Table
        columns={tab === "Deleted" ? deletedColumns : activeInactiveColumns}
        data={clickableData}
        actions={actions}
        rowsPerPage={rowsPerPage}
        serverSidePagination={true}
        totalRecords={totalCount}
        onPageChange={(page) =>
          setPageMap((prev) => ({
            ...prev,
            [tab]: page,
          }))
        }
        loading={loading}
      />
      {menuOpen && selectedRow && (
        <MenuItem
          open={menuOpen}
          onClose={handleCloseMenu}
          align="left"
          options={
            tab === "Deleted"
              ? [
                  {
                    label: `Activate ${selectedRow.name}`,
                    onClick: () =>
                      handleOpenConfirmModal("activate", selectedRow),
                  },
                ]
              : [
                  {
                    label: `Delete ${selectedRow.name}`,
                    onClick: () =>
                      handleOpenConfirmModal("delete", selectedRow),
                  },
                ]
          }
          style={{
            position: "absolute",
            top: `${menuPosition.top}px`,
            right: `${menuPosition.right}px`,
            zIndex: 1000,
          }}
        />
      )}
      {isModalOpen && selectedUser && (
        <UserModal
          type={modalType}
          name={selectedUser.name}
          studyId={selectedUser.studyId}
          loading={
            modalType === "message"
              ? messageCreateLoading
              : feedbackCreateLoading
          }
          onClose={handleCloseModal}
          onSend={handleSend}
        />
      )}
      {isConfirmModalOpen && confirmUser && (
        <ConfirmUserActionModal
          type={confirmActionType}
          userName={confirmUser.name}
          onClose={() => setIsConfirmModalOpen(false)}
          onConfirm={handleConfirmAction}
        />
      )}
      {isEditModalOpen && editUser && (
        <EditUserModal
          user={editUser}
          onClose={() => setIsEditModalOpen(false)}
          loading={updateUserLoading}
          onUpdate={async (data) => {
            if (updateUserLoading) return;
            try {
              await dispatch(
                updateUser({
                  userId: editUser.id,
                  payload: {
                    firstName: data.firstName,
                    lastName: data.lastName,
                    email: data.email,
                    mobileNumber: data.mobileNumber,
                  },
                }),
              ).unwrap();

              setIsEditModalOpen(false);

              dispatch(
                fetchUserList({
                  pageId: pageMap[tab],
                  limit: rowsPerPage,
                  status: getApiStatus(),
                }),
              );
            } catch (err) {
              console.error("Update failed:", err);
            }
          }}
        />
      )}
    </div>
  );
};

export default Users;
